import * as React from "react";

export const VIEWPORT_BREAKPOINTS = {
  mobile: 768,
  tabletLg: 1024,
  desktop: 1280,
} as const;

export type Breakpoint = "mobile" | "tablet" | "tablet-lg" | "desktop";

function computeBreakpoint(width: number): Breakpoint {
  if (width < VIEWPORT_BREAKPOINTS.mobile) return "mobile";
  if (width < VIEWPORT_BREAKPOINTS.tabletLg) return "tablet";
  if (width < VIEWPORT_BREAKPOINTS.desktop) return "tablet-lg";
  return "desktop";
}

function readBreakpoint(): Breakpoint {
  if (typeof window === "undefined") return "desktop";
  return computeBreakpoint(window.innerWidth);
}

const listeners = new Set<(bp: Breakpoint) => void>();
let currentBreakpoint: Breakpoint | null = null;
let resizeBound = false;

function ensureResizeListener() {
  if (resizeBound || typeof window === "undefined") return;
  resizeBound = true;
  currentBreakpoint = readBreakpoint();
  const onResize = () => {
    const next = readBreakpoint();
    if (next !== currentBreakpoint) {
      currentBreakpoint = next;
      listeners.forEach((fn) => fn(next));
    }
  };
  window.addEventListener("resize", onResize);
  window.addEventListener("orientationchange", onResize);
}

export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = React.useState<Breakpoint>(() => readBreakpoint());

  React.useEffect(() => {
    ensureResizeListener();
    setBp(readBreakpoint());
    listeners.add(setBp);
    return () => {
      listeners.delete(setBp);
    };
  }, []);

  return bp;
}

export function useIsMobile(): boolean {
  return useBreakpoint() === "mobile";
}

export function useIsTablet(): boolean {
  const bp = useBreakpoint();
  return bp === "tablet" || bp === "tablet-lg";
}

export function useIsTabletPortrait(): boolean {
  return useBreakpoint() === "tablet";
}

export function useIsDesktop(): boolean {
  return useBreakpoint() === "desktop";
}
